import React from 'react';
import { NextPageContext, NextPage } from 'next';
import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { useRouter } from 'next/router';
import dynamic, { LoaderComponent } from 'next/dynamic';
import { IncomingMessage, ServerResponse } from 'node:http';
import { useSiteContext } from '../components/SiteContext';
import { JobDetailWithDataProperties } from '../components/Jobs/JobDetail';
import withApollo from '../lib/apollo';
import getJobsLocale from '../lib/getJobsLocale';
import slugQuery from '../lib/queries/jobs/jobSlugQuery.graphql';
import loading from '../lib/loading';
import redirectJobSlug from '../lib/redirectJobSlug';

const JobDetail = dynamic(() => import(
  '../components/Jobs/JobDetail'
) as unknown as LoaderComponent<JobDetailWithDataProperties>, {
  ssr: true,
  loading,
});

export const JobPage: NextPage = () => {
  const { query: { id } } = useRouter();
  const { jobsLocale } = useSiteContext();

  return (
    <JobDetail
      locale={jobsLocale}
      id={Number(id)}
    />
  );
};

interface MyIncomingMessage extends IncomingMessage {
  locale?: string;
}

interface MyServerResponse extends ServerResponse {
  status?: (statusCode: number) => MyServerResponse;
}

export interface MyPageContext extends NextPageContext {
  req?: MyIncomingMessage;
  res?: MyServerResponse;
  apolloClient: ApolloClient<NormalizedCacheObject>;
}

JobPage.getInitialProps = async (context: MyPageContext) => {
  const {
    req, apolloClient, query: { id, slug, prefix }, res,
  } = context;

  if (!req) {
    return {};
  }

  const { locale } = req;
  const { data: { job } } = await apolloClient.query({
    query: slugQuery,
    variables: {
      locales: [getJobsLocale(locale)],
      id: Number(id),
    },
    fetchPolicy: 'network-only',
  });

  if (!job && res?.status) {
    res.status(410);
  }

  return redirectJobSlug(
    job,
    slug,
    id,
    prefix,
    `job.${locale}`,
    res,
  );
};

export default withApollo(JobPage);
